import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/Button';
import { X } from 'lucide-react';
import { KeyResultType } from '@momentum/types';
import { RecurrenceConfigField } from './RecurrenceConfigField';

export const defaultKeyResult = {
  title: '',
  unit: '',
  quantity: 0,
  currentProgress: 0,
  type: KeyResultType.MAXIMUM,
};

interface KeyResultFieldProps {
  index: number;
  fields: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  onRemove: () => void;
}

export const KeyResultField: React.FC<KeyResultFieldProps> = ({
  index,
  fields,
  form,
  onRemove,
}) => {
  const { t } = useTranslation('goals');

  return (
    <div className="relative group/item">
      <div className="p-5 border border-border rounded-lg bg-card shadow-sm hover:shadow-md transition-all duration-200 space-y-4">
        {/* Header with Index and Remove Button */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold">
              {index + 1}
            </div>
            <span className="text-sm font-medium text-muted-foreground">
              {t('keyResult')}
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="h-8 w-8 p-0 opacity-0 group-hover/item:opacity-100 transition-opacity hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {/* Title Field */}
        <div className="space-y-2">
          <form.AppField
            name={`${fields}.title`}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            children={(field: any) => (
              <div className="relative">
                <field.TextField
                  label={t('title')}
                  placeholder={t('keyResultTitlePlaceholder')}
                />
              </div>
            )}
          />
        </div>
        {/* Type Field */}
        <div className="space-y-2">
          <form.AppField
            name={`${fields}.type`}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            children={(field: any) => (
              <field.SelectField
                label={t('keyResultType')}
                placeholder={t('selectKeyResultType')}
                options={[
                  { value: KeyResultType.MAXIMUM, label: t('maximumType') },
                  { value: KeyResultType.MINIMUM, label: t('minimumType') },
                ]}
              />
            )}
          />
        </div>
        {/* Quantity and Unit Fields */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <form.AppField
              name={`${fields}.quantity`}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              children={(field: any) => (
                <field.NumberField
                  label={t('targetQuantity')}
                  placeholder="100"
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <form.AppField
              name={`${fields}.unit`}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              children={(field: any) => (
                <field.TextField
                  label={t('unit')}
                  placeholder={t('unitPlaceholder')}
                />
              )}
            />
          </div>
        </div>
        {/* Current Progress Field */}
        <div className="space-y-2">
          <form.AppField
            name={`${fields}.currentProgress`}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            children={(field: any) => (
              <field.NumberField
                label={t('currentProgress')}
                placeholder="0"
                min={0}
              />
            )}
          />
        </div>

        {/* Recurrence Configuration */}
        <RecurrenceConfigField
          form={form}
          fieldPath={`${fields}.recurrence`}
          label="repeatableKeyResult"
        />
      </div>
    </div>
  );
};
